import { validate as isUuid } from 'uuid';

import { markPagePreviewUrl } from '@/lib/pagePreviewUrl';

import { buildUrlFromSettings, resolveUrlTarget } from './routeUrl';
import type {
  TaskUtils,
  TaskWorkflowTarget,
  WorkflowRouteSettings,
  WorkflowRouteSettingsEntry
} from './types';
import { warnSdkUtilFallback } from './warn';

type WorkflowRouteSettingsMap<TEntry extends WorkflowRouteSettingsEntry> =
  | Record<string, TEntry | undefined>
  | null
  | undefined;

export function buildWorkflowSlugIndex<TEntry extends WorkflowRouteSettingsEntry>(
  settings: WorkflowRouteSettingsMap<TEntry>
): Record<string, string> {
  const index = Object.create(null) as Record<string, string>;

  for (const [workflowId, routeSettings] of Object.entries(settings ?? {})) {
    const slug = routeSettings?.slug;

    if (!slug || Object.hasOwn(settings ?? {}, slug)) {
      continue;
    }

    index[slug] = workflowId;
  }

  return index;
}

export function resolveWorkflowRouteSettings<TEntry extends WorkflowRouteSettingsEntry>(
  settings: WorkflowRouteSettingsMap<TEntry>,
  reference: string,
  slugIndex = buildWorkflowSlugIndex(settings)
): { workflowId: string; settings: TEntry } | null {
  const directSettings =
    settings && Object.hasOwn(settings, reference) ? settings[reference] : undefined;

  if (directSettings) {
    return { workflowId: reference, settings: directSettings };
  }

  if (isUuid(reference)) {
    return null;
  }

  const workflowId = slugIndex[reference];
  const resolvedSettings =
    workflowId && settings && Object.hasOwn(settings, workflowId)
      ? settings[workflowId]
      : undefined;

  return workflowId && resolvedSettings ? { workflowId, settings: resolvedSettings } : null;
}

export function buildWorkflowUrlFromSettings(
  routeSettings: WorkflowRouteSettingsEntry | undefined,
  workflowId: string,
  params: Record<string, unknown> = {}
): string {
  const path = buildUrlFromSettings(routeSettings, 'Workflow', workflowId, params);

  if (routeSettings?.origin === null) {
    throw new Error(`Workflow '${workflowId}' does not have an active domain.`);
  }

  return routeSettings?.origin
    ? `${routeSettings.origin}${path.startsWith('/') ? path : `/${path}`}`
    : path;
}

export function createWorkflowUtils(
  settings: WorkflowRouteSettings,
  slugIndex = buildWorkflowSlugIndex(settings)
): TaskUtils['workflow'] {
  return {
    url: (target: TaskWorkflowTarget, paramsArg?: Record<string, unknown>) => {
      try {
        const { id, params } = resolveUrlTarget(
          target,
          paramsArg,
          'Sdk.utils.workflow.url requires a workflow slug or ID.'
        );

        const resolved = resolveWorkflowRouteSettings(settings, id, slugIndex);

        return markPagePreviewUrl(
          buildWorkflowUrlFromSettings(resolved?.settings, resolved?.workflowId ?? id, params)
        );
      } catch (error) {
        const reason =
          error instanceof Error ? error.message : 'The workflow URL could not be resolved.';
        warnSdkUtilFallback('workflow.url', `${reason} Returning null.`);
        return null;
      }
    }
  };
}
